import { getStakingEnv } from "./env.js";

/**
 * Trait spin layer id from OwlexTraitSpin `layerCloudflareId` → Cloudflare Images URL.
 * Set `VITE_CF_IMAGES_BASE_URL` to `https://imagedelivery.net/<your_account_hash>` in env.
 * @param {string} cloudflareId
 * @returns {string}
 */
export function cloudflareTraitLayerImageUrl(cloudflareId) {
  const id = String(cloudflareId ?? "").trim();
  if (!id) return "";
  const { cfImagesBaseUrl, cfImagesVariant } = getStakingEnv();
  const base = String(cfImagesBaseUrl ?? "").trim().replace(/\/+$/, "");
  if (!base) return "";
  const v = String(cfImagesVariant ?? "public").trim() || "public";
  return `${base}/${encodeURIComponent(id)}/${encodeURIComponent(v)}`;
}

/** URL-safe trait folder segment for compositor `/r2/<trait>/...` (hyphen slug). */
export function traitTypePathSegment(canonicalName) {
  return String(canonicalName).trim().replace(/\s+/g, "-");
}

function traitSlug(canonical) {
  return String(canonical).replace(/\s+/g, "-");
}

function normalizeTraitValue(s) {
  return String(s ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200b-\u200d\ufeff]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Filename stems to try (mirrors workers/owlex-compositor `layerFilenameStems`).
 * @param {string} value
 * @returns {string[]}
 */
export function layerFilenameStems(value) {
  const trimmed = normalizeTraitValue(value);
  /** @type {string[]} */
  const stems = [];
  const seen = new Set();
  const add = (raw) => {
    const stem = raw.replace(/\.(png|webp|jpe?g)$/i, "").trim();
    if (!stem || seen.has(stem)) return;
    seen.add(stem);
    stems.push(stem);
  };

  add(trimmed);
  const slug = trimmed.replace(/\s+/g, "-");
  const unders = trimmed.replace(/\s+/g, "_");
  const nospace = trimmed.replace(/\s+/g, "");
  add(slug);
  add(slug.toLowerCase());
  add(unders);
  add(nospace);
  add(trimmed.toLowerCase());
  if (trimmed.toUpperCase() !== trimmed) add(trimmed.toUpperCase());

  return stems;
}

/**
 * Folder names to try under `custom-layers/` (mirrors `r2TraitFolderAttempts` in compositor).
 * @param {string} canonical
 * @returns {string[]}
 */
export function metadataFolderAttempts(canonical) {
  const c = String(canonical ?? "").trim();
  const out = [];
  const seen = new Set();
  const push = (s) => {
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };

  push(c);
  const slug = traitSlug(c);
  if (slug !== c) push(slug);
  const noSpace = c.replace(/\s+/g, "");
  if (noSpace !== c && noSpace !== slug) push(noSpace);

  if (!/\s/.test(c)) {
    const title = c.charAt(0).toUpperCase() + c.slice(1).toLowerCase();
    if (title !== c) push(title);
    const lower = c.toLowerCase();
    if (lower !== c) push(lower);
    const upper = c.toUpperCase();
    if (upper !== c) push(upper);
  } else {
    const lower = c.toLowerCase();
    if (lower !== c) push(lower);
  }

  return out;
}

function compositorOrigin() {
  const raw = getStakingEnv().compositorUrl || "/api/compositor";
  const trimmed = String(raw).trim().replace(/\/+$/, "");
  if (trimmed.startsWith("/")) {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}${trimmed}`;
  }
  return trimmed;
}

function metadataOriginBase() {
  return getStakingEnv().metadataOrigin?.replace(/\/+$/, "") || "";
}

/**
 * Public CDN: `{metadataOrigin}/custom-layers/{folder}/{file}`
 * @param {string} folder
 * @param {string} fileWithExt
 */
/**
 * @param {'custom-layers' | 'all-layers'} layerPrefix
 * @param {string} folder
 * @param {string} fileWithExt
 */
function metadataLayerFileUrl(layerPrefix, folder, fileWithExt) {
  const base = metadataOriginBase();
  if (!base || !folder || !fileWithExt || !layerPrefix) return "";
  const prefix = layerPrefix.replace(/^\/+|\/+$/g, "");
  return `${base}/${prefix}/${encodeURIComponent(folder)}/${encodeURIComponent(fileWithExt)}`;
}

function metadataCustomLayerFileUrl(folder, fileWithExt) {
  return metadataLayerFileUrl("custom-layers", folder, fileWithExt);
}

const RASTER_EXTS = ["png", "webp", "jpg", "jpeg"];

/** Max URLs embedded in DOM (each failure triggers `error` → next). */
const MAX_CANDIDATE_URLS = 72;

/**
 * Ordered URLs for trait layer images.
 *
 * @param {string} canonicalTraitTypeName
 * @param {string} traitValueName
 * @param {{ layerSource?: 'custom-layers' | 'all-layers', allowCompositorFallback?: boolean }} [options]
 * @returns {string[]}
 */
export function buildTraitLayerImageCandidates(canonicalTraitTypeName, traitValueName, options = {}) {
  const layerSource = options.layerSource === "all-layers" ? "all-layers" : "custom-layers";
  const allowCompositor = options.allowCompositorFallback !== false && layerSource === "custom-layers";
  const base = metadataOriginBase();
  const comp = compositorOrigin();
  const folders = metadataFolderAttempts(canonicalTraitTypeName);
  const stems = layerFilenameStems(traitValueName);

  /** @type {string[]} */
  const out = [];
  const push = (u) => {
    if (typeof u === "string" && u.length > 0 && !out.includes(u) && out.length < MAX_CANDIDATE_URLS) {
      out.push(u);
    }
  };

  if (stems.length === 0) return out;

  if (base) {
    for (const ext of RASTER_EXTS) {
      for (const folder of folders) {
        for (const stem of stems) {
          push(metadataLayerFileUrl(layerSource, folder, `${stem}.${ext}`));
        }
      }
    }
  }

  if (allowCompositor && comp) {
    const seg = traitTypePathSegment(canonicalTraitTypeName);
    for (const ext of RASTER_EXTS) {
      for (const stem of stems) {
        const file = `${stem}.${ext}`;
        push(`${comp}/r2/${seg}/${encodeURIComponent(file)}`);
      }
    }
  }

  return out;
}

/** Neutral placeholder when every real URL fails (not the Owlex logo). */
export const TRAIT_IMAGE_MISSING_DATA_URI =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="140" height="140" viewBox="0 0 140 140"><rect fill="#e8eaef" width="140" height="140" rx="10"/><path fill="#94a3b8" fill-rule="evenodd" d="M45 52c0-4 3-8 8-8h34c4 0 8 3 8 8v26c0 4-3 8-8 8H53c-5 0-8-4-8-8V52zm8 0v26h34V52H53zm13 36c-7 0-12 5-12 12v4h46v-4c0-7-5-12-12-12H66z"/></svg>`
  );

/**
 * Encode candidate list for a safe `data-trait-img-urls` attribute (avoids broken JSON from quotes).
 * @param {string[]} urls
 */
export function encodeTraitImageCandidatesForDom(urls) {
  return encodeURIComponent(JSON.stringify(urls));
}

function parseTraitImageCandidateList(img) {
  const enc = img.dataset.traitImgUrls;
  if (enc != null && enc !== "") {
    try {
      const list = JSON.parse(decodeURIComponent(enc));
      if (Array.isArray(list) && list.length > 0) return list;
    } catch {
      try {
        const list = JSON.parse(enc);
        if (Array.isArray(list) && list.length > 0) return list;
      } catch {
        /* fall through */
      }
    }
  }
  const raw = img.dataset.traitImgCandidates;
  if (raw == null || raw === "") return null;
  try {
    const list = JSON.parse(raw);
    return Array.isArray(list) && list.length > 0 ? list : null;
  } catch {
    return null;
  }
}

/**
 * Advance `img.src` through candidate list on decode/load error.
 * @param {HTMLImageElement} img
 * @returns {boolean} true if handled (advanced or set neutral placeholder)
 */
export function advanceTraitLayerImageOnError(img) {
  const list = parseTraitImageCandidateList(img);
  if (!list) return false;

  let i = Number(img.dataset.traitImgI || 0);
  i += 1;
  if (i < list.length) {
    img.dataset.traitImgI = String(i);
    img.src = list[i];
    return true;
  }
  if (img.dataset.traitImgFallback !== "1") {
    img.dataset.traitImgFallback = "1";
    img.src = TRAIT_IMAGE_MISSING_DATA_URI;
    return true;
  }
  return true;
}

/**
 * Preview image for compositor `/r2/...` (single-variant helpers for callers that do not use {@link buildTraitLayerImageCandidates}).
 * @param {string} canonicalTraitTypeName
 * @param {string} traitValueName
 */
export function traitAssetUrlPng(canonicalTraitTypeName, traitValueName) {
  const origin = compositorOrigin();
  if (!origin) return "";
  const stems = layerFilenameStems(traitValueName);
  const stem = stems[0] ?? "";
  if (!stem) return "";
  const traitSeg = traitTypePathSegment(canonicalTraitTypeName);
  return `${origin}/r2/${traitSeg}/${encodeURIComponent(`${stem}.png`)}`;
}

/** @param {string} canonicalTraitTypeName
 * @param {string} traitValueName */
export function traitAssetUrlWebp(canonicalTraitTypeName, traitValueName) {
  const origin = compositorOrigin();
  if (!origin) return "";
  const stems = layerFilenameStems(traitValueName);
  const stem = stems[0] ?? "";
  if (!stem) return "";
  const traitSeg = traitTypePathSegment(canonicalTraitTypeName);
  return `${origin}/r2/${traitSeg}/${encodeURIComponent(`${stem}.webp`)}`;
}
